import React, {useEffect, useState, useRef} from "react";
import styled from "styled-components";
import {t} from "../../shared-services/translate";
import {useSelector, useTopLayerActions} from "../../shared-services/redux-tiny";
import {cartTools} from "./useCart";
import currentTheme from "../../react-themes/theme";
import {mediaQuery} from "../../react-components/shared/GlobalStyles";
import BasicSpinner from "../loaders/BasicSpinner";
import {useHistory, Link} from "react-router-dom";
import useTracking from "../../react-services/useTracking";
import useWindowTools from "../../react-hooks/useWindowTools";

import DialogPDP from "../products/ProductPage/DialogPDP";
import UnavailableItemsList from "./Unavailable.ItemsList";
import UnavailableCTA from "./Unavailable.CTA";

const theme = currentTheme();
const loaderTime = 700;
const decimals = 2;

const CTAButton = ({currentCart, className = ""}) => {
  const {currentUser} = useSelector(state => state.session);
  const {isLoading} = useSelector(state => state.cart);
  const {bottomNotification} = useSelector(state => state.topLayer);

  const [btnDisabled, setBtnDisabled] = useState(true);
  const [showLoader, setShowLoader] = useState(false);
  const [currentTotal, setCurrentTotal] = useState(0);
  const [displayValue, setDisplayValue] = useState("0.00");
  const [submitUrl, setSubmitUrl] = useState("#");
  const [ctaTextKey, setCtaTextKey] = useState("login_to_continue");
  const [showDialog, setShowDialog] = useState(false);

  const btnRef = useRef(false);
  const {trackEvent} = useTracking();
  const {setBottomNotification, emptyBottomNotification} = useTopLayerActions();
  const history = useHistory();
  const {isMobile} = useWindowTools();
  const {hasUnavailableItems} = cartTools();

  const minimumValueReached = () => {
    return currentCart?.item_total >= currentCart?.minimum_order_value;
  };

  const totalsWereChanged = () => {
    return currentCart?.cart_summary?.item_total_incl_wc_surcharge !== currentTotal;
  };

  const onDialogClose = () => setShowDialog(false);

  const onRemove = () => {
    history.push(getSubmitUrl());
  };

  const toggleBottomNotification = () => {
    const notificationName = "BottomUnavailableItems";
    const options = {currentCart, onRemove};

    if (bottomNotification?.name === notificationName) emptyBottomNotification();
    else {
      setBottomNotification({
        name: notificationName,
        options
      });
    }
  };

  const trackGoToCheckout = () => {
    if (btnDisabled || showLoader || isLoading) return;
    trackEvent(
      "begin_checkout",
      {
        products: currentCart?.line_items,
        checkout: currentCart
      });
  };

  const getSubmitUrl = () => {
    const isLoggedIn = !!currentUser?.id;
    const url = isLoggedIn ? "/cashier" : "/signup?checkout=t&login=t";

    return (btnDisabled || showLoader || isLoading) ? "#" : url;
  };

  useEffect(() => {
    let loaderTimeout;
    
    if (totalsWereChanged()) {
      setCurrentTotal(currentCart?.cart_summary?.item_total_incl_wc_surcharge);

      if (currentCart?.minimum_order_value && !minimumValueReached()) {
        const value = (currentCart?.minimum_order_value - currentCart?.item_total).toFixed(decimals);
        setDisplayValue(value);
      } else {
        setDisplayValue(currentCart.cart_summary.item_total_incl_wc_surcharge?.toFixed(decimals));
      }

      if (isLoading) {
        setShowLoader(true);

        loaderTimeout = setTimeout(() => {
          requestAnimationFrame(() => {
            if (btnRef.current) setShowLoader(false);
          });
        }, loaderTime);
      } else setShowLoader(false);
    }

    if (minimumValueReached() && !hasUnavailableItems(currentCart)) {
      setBtnDisabled(false);
    } else {
      setBtnDisabled(true);
    }
  }, [currentCart, isLoading]);

  useEffect(() => {
    const isLoggedIn = !!currentUser?.id;
    let actionTextKey;

    if (!minimumValueReached()) {
      actionTextKey = "minimum_value_not_reached";
    } else {
      actionTextKey = isLoggedIn ? "continue_cta" : "login_to_continue";
    }

    setSubmitUrl(getSubmitUrl());
    setCtaTextKey(actionTextKey);
  }, [currentUser?.id, btnDisabled, showLoader, isLoading]);

  useEffect(() => {
    btnRef.current = true;

    return () => {
      btnRef.current = false;
    }
  }, []);

  return <div
    className={`position-relative ${className}`}
    onMouseEnter={() => !isMobile && setShowDialog(true)}
    onClick={() => isMobile && hasUnavailableItems(currentCart) && toggleBottomNotification()}
  >
    {!isMobile && hasUnavailableItems(currentCart) &&
      <DialogPDP
        show={showDialog}
        onClose={onDialogClose}
        position="bottom"
        hideCloseBtn={true}
        className="background-color-white"
        style={{padding: "20px"}}
      >
        <div className="margin-bottom-20 font-weight-900">
          {t("products.unavailable")}:
        </div>
        <UnavailableItemsList order={currentCart}/>
        <UnavailableCTA order={currentCart} onRemove={onRemove}/>
      </DialogPDP>
    }

    <Link
      to={submitUrl}
      onClick={trackGoToCheckout}
    >
      <StyledCTA
        className={`${isMobile ? "btn btn-primary " : ""}flex justify-content-space-between align-items-center`}
        disabled={btnDisabled || showLoader || isLoading}
      >
        {(showLoader || isLoading) && <div className="loader flex justify-content-center align-items-center full-width full-height">
          <BasicSpinner small color={theme.colors.muteTextColor}/>
        </div>}
        <div className={`full-width flex align-items-center ${ctaTextKey === "minimum_value_not_reached" ? "justify-content-center" : "justify-content-space-between"}`}
          dangerouslySetInnerHTML={{__html: t(`cart.cta.${ctaTextKey}`, {amount: displayValue})}}
        ></div>
      </StyledCTA>
    </Link>
    {!minimumValueReached() && ctaTextKey === "minimum_value_not_reached" &&
      <StyledCTASuffix className="cta-extra-content full-width text-align-center font-family-alata">
        {t(`cart.cta.${ctaTextKey}_suffix`)}
      </StyledCTASuffix>}
  </div>;
};

export default CTAButton;

export const StyledCTA = styled.button`
  background-color: ${theme.colors.primaryBg};
  border: 2px solid ${theme.colors.primaryBg};
  color: #FFFFFF;
  padding: 9px 20px;
  border-radius: 5px;
  cursor: pointer;
  width: 100%;
  height: 50px;
  min-height: 50px;
  font-weight: 900;
  font-size: 20px;
  text-shadow: none;
  position: relative;
  &:disabled {
    background-color: transparent;
    cursor: not-allowed;
    border: 2px solid ${theme.colors.muteTextColor};
    color: ${theme.colors.muteTextColor};
  }
  .loader {
    position: absolute;
    top: 0;
    left: 0;
    background-color: #FFFFFF;
    opacity: 0.5;
    z-index: 500;
  }
  .total-number {
    sup {
      display: none;
      ${mediaQuery("sm")`
        display: inline;
      `}
    }
    span {
      ${mediaQuery("sm")`
        display: none;
      `}
    }
  }
  @media (max-width: 360px) {
    font-size: 18px;
  }
  @media (max-width: 310px) {
    font-size: 16px;
  }
`;

export const StyledCTASuffix = styled.div`
  min-height: 18px;
  height: 18px;
  font-size: 14px;
`;
